import type { ItemFieldRow } from './item-field-reader';
import { isOwnedRuleWindow } from './owned-rule-window';
import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createSkillRequirementChecker } from './skill-requirements';
import { meetsAbilityLevelRequirements } from './ability-level-requirement-gate';
import { readAbilityEntryId, readAbilityEntryLayerSource, containsAbilityTriggerKey } from './inventory-ability-grant-helpers';
import { readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { appendCompiledLayerInPlace } from './status-row-construction';
import { materializeCreatureAbilityEntry } from './creature-ability-entry';
import { compileDefaultAbilityGrants } from './default-ability-grants';
import { finalizeNonChargeAbilityBucket } from './non-charge-ability-bucket';
import { groupInnateSpells } from './innate-spell-grouping';
import { compileCreatureOverrunChargesRemaining } from './creature-overrun-charges';
import { resolveFormulaVariables } from './formula-variable-resolution';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { nativePhpJson, fromJson, PhpArray } from '../php-compat';
import { readCreatureStatuses } from './creature-status-reader';
import { createPlayerDefinitionFinalizer } from './from-base-player-sections';
import { materializePlayerPreparedSpells } from './player-prepared-spell-materialization';
import { ftProjectFeatGrantedExecutableAbilities, ftProjectCarrierAndPassiveHostedSpellGrants,
    ftSpellGrantRowSources, ftSpellGrantSourceCounted, ftHoistBucketedSpellsKnown } from '../port/k1-compile/core/finalize_creature_abilities';
import type { FormulaVariableReaders } from './formula-variable-resolution';

const executable = ['full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'];
const SHORT_REST = '4744CC19-87A9-41D7-9285-ABD36470EC81';

/** Shared lean spell-book projection for both a full construction and a preparation delta. */
export function playerSpellKnownPointers(rows: readonly FromBaseRow[], player: FromBaseRow, readers: FormulaVariableReaders,
    definition: (id: string) => ItemFieldRow | null): FromBaseRow[] {
    const deriveClass = (spell: string): string => {
        const matches = new Set<string>();
        for (let slot = 1; slot <= 20; slot++) {
            const raw = readers.itemField(player, `class_${slot}`);
            const id = readItemUuidSlot(raw);
            const provider = fromBaseRecord(raw) ? raw : fromBaseUuid(id) ? definition(id) : null;
            if (!fromBaseRecord(provider)) continue;
            if (Object.values(readers.layerEntries(provider, 'spell_known')).some(entry => readLayerListEntryUuid(entry).toUpperCase() === spell.toUpperCase()))
                matches.add(phpJsonString(readers.itemField(provider, 'item_id')).toUpperCase());
        }
        return matches.size === 1 ? [...matches][0]! : '';
    };
    return rows.map(row => {
        const id = phpJsonString(readers.itemField(row, 'item_id')), out: FromBaseRow = {};
        const owner = readItemUuidSlot(readers.layerValue(row, 'class_id')), subclass = readItemUuidSlot(readers.layerValue(row, 'subclass_id'));
        for (const [key, ref] of [['class', owner && deriveClass(id) !== owner.toUpperCase() ? owner : ''], ['subclass', subclass]]) {
            const def = ref ? definition(ref) : null;
            if (def) out[key!] = { items: Object.fromEntries(['item_id', 'item_name', 'item_type'].map(name => [name, readers.itemField(def, name)])) };
        }
        out.item_id = id; return out;
    });
}

/** Only the native prepared-row projection enters the ability carrier. */
export function constructPlayerPreparedSpellEntry(row: ItemFieldRow, spell: FromBaseRow, subclassIds: readonly string[],
    value: (row: ItemFieldRow | null, key: string) => unknown): FromBaseRow {
    return { class_id: value(row, 'class_id'), spell_list: value(row, 'spell_list'), spell_id: value(row, 'spell_id'),
        subclass_id: subclassIds, ...spell };
}

/** Saved counts and authored ceilings share one reader for own and provider grants. */
export function createActorAbilityMaterializer(base: { entity_id: string; charges?: readonly FromBaseRow[] },
    metadata: FromBasePlayerResourceMetadata, host: FromBaseRow,
    { readers, definition }: Pick<ReturnType<typeof createFromBasePlayerGrantContext>, 'readers' | 'definition'>) {
    const stored = (row: ItemFieldRow | null, key: string): unknown => readers.itemField({ [key]: [row ? (row as FromBaseRow)[key] ?? null : null] }, key);
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const list = (row: ItemFieldRow | null, key: string): unknown[] => { const slot = stored(row, key); return slot !== null && typeof slot === 'object' ? Object.values(slot) : []; };
    const has = (row: ItemFieldRow | null, key: string) => readers.layerValue(row, key) !== null || entries(row, key).length > 0;
    const creatureId = readers.layerValue(host.encounter_creatures as ItemFieldRow ?? {}, 'encounter_creature_id');
    if (!Array.isArray(base.charges)) throw new FromBaseInputMissing('saved-charge-rows', base.entity_id);
    const charge = (id: string, fallback: unknown): unknown => {
        const row = base.charges!.find(raw => phpJsonString(readers.layerValue(raw, 'charge_holder')).toUpperCase() === phpJsonString(creatureId).toUpperCase()
            && phpJsonString(readers.layerValue(raw, 'item_id')).toUpperCase() === id.toUpperCase() && phpJsonInt(readers.layerValue(raw, 'charge_deleted')) === 0);
        return row ? readers.layerValue(row, 'charges') : fallback;
    };
    const maximum = (id: string, ability: ItemFieldRow): number | string => {
        let total = resolveFormulaVariables(readers.layerValue(ability, 'ability_charges'), host, '', metadata.rollup, readers);
        if (!isPhpNumeric(total)) return total;
        total = phpJsonInt(total);
        for (const type of ['passive_ability', 'feat']) for (const entry of list(host, type).length ? list(host, type) : list(host.item_layer_types as ItemFieldRow, type)) {
            const carrier = typeof entry === 'string' ? definition(entry) : fromBaseRecord(entry) ? readAbilityEntryLayerSource(entry, { ...readers, resolveCarrierRef: definition }) : null;
            if (!carrier) continue;
            for (const [action, quantity, sign] of [['increase_max_charges_of_ability', 'increase_max_charges_by_qty', 1],
                ['decrease_max_charges_of_ability', 'decrease_max_charges_by_qty', -1]] as const)
                if (new Set(entries(carrier, action).map(readLayerListEntryUuid).map(value => value.toUpperCase())).has(id.toUpperCase()))
                    total += phpJsonInt(resolveFormulaVariables(readers.layerValue(carrier, quantity) ?? 1, host, '', metadata.rollup, readers)) * sign;
        }
        return total;
    };
    const materialize = (entry: unknown, id: string, ability: ItemFieldRow | null) => {
        const rawSource = fromBaseRecord(entry) ? stored(entry, 'source') : null;
        const source = fromBaseRecord(rawSource) ? stored(rawSource, 'source') ?? 'NA' : rawSource ?? 'NA';
        const max = ability && has(ability, 'ability_charges') ? maximum(id, ability) : null;
        return materializeCreatureAbilityEntry({ abilityToken: id, source: typeof source === 'object' && source !== null ? source as { source?: string } : phpJsonString(source),
            requiresLevel: (fromBaseRecord(entry) ? stored(entry, 'requires_level') ?? 1 : 1) as string | number,
            consumeNestedUnitPerUse: fromBaseRecord(entry) && has(entry, 'consume_nested_unit_per_use'),
            charges: max === null ? null : { current: charge(id, max) as number | string | null, max,
                resetsLongRest: has(ability, 'ability_resets_long_rest'), rechargeRangeBegin: readers.layerValue(ability, 'recharge_range_begin') as number | string | null,
                rechargeRangeEnd: readers.layerValue(ability, 'recharge_range_end') as number | string | null } });
    };
    return { charge, materialize };
}

/** Native PC ability tail over locally constructed buckets and saved charge rows.
 * Projections share the K1 ports; the entry materializer owns charge elision. */
export function constructPlayerAbilitiesFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, input: FromBaseRow): FromBaseRow {
    return constructActorAbilitiesFromBase(base, metadata, input,
        createFromBasePlayerGrantContext(base, metadata, input, 'ability-spell-finalization'), base);
}

/** Shared live actor materialization; PC class/prepared/short-rest inputs stay explicit. */
export function constructActorAbilitiesFromBase(base: { entity_id: string; charges?: readonly FromBaseRow[] },
    metadata: FromBasePlayerResourceMetadata, input: FromBaseRow,
    context: {
        readers: ReturnType<typeof createFromBasePlayerGrantContext>['readers'];
        definition: (id: string) => ItemFieldRow | null;
        deps: Parameters<typeof createAbilityConditionEvaluator>[0] & Parameters<typeof createSkillRequirementChecker>[0];
    },
    playerBase?: FromBasePlayerInput): FromBaseRow {
    const owned = isOwnedRuleWindow(input);
    let host = fromBaseClone(input);
    const { readers, definition, deps } = context;
    const stored = (row: ItemFieldRow | null, key: string): unknown => readers.itemField({ [key]: [row ? (row as FromBaseRow)[key] ?? null : null] }, key);
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const list = (row: ItemFieldRow | null, key: string): unknown[] => { const slot = stored(row, key); return slot !== null && typeof slot === 'object' ? Object.values(slot) : []; };
    const has = (row: ItemFieldRow | null, key: string) => readers.layerValue(row, key) !== null || entries(row, key).length > 0;
    const idOf = (entry: unknown) => readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition });
    const resolve = (entry: unknown): ItemFieldRow | null => {
        const nested = fromBaseRecord(entry) ? stored(entry, 'item_arr') : null;
        const row = nested !== null && typeof nested === 'object' ? nested : entry;
        if (row === null || typeof row !== 'string' && typeof row !== 'object') return null;
        return (layerEntryNeedsResolve(row) ? resolveLayerEntry(row, host, definition) : row) as ItemFieldRow;
    };
    const conditions = createAbilityConditionEvaluator(deps), skillGate = createSkillRequirementChecker(deps);
    const sourceOf = (entry: unknown, fallback: string): unknown => {
        const source = fromBaseRecord(entry) ? stored(entry, 'source') : null;
        return fromBaseRecord(source) ? stored(source, 'source') ?? fallback : source ?? fallback;
    };
    const sourceItemId = phpJsonString(readers.itemField(host, 'item_id') ?? base.entity_id);
    const classLevels = new Map<string, number>();
    if (playerBase) for (let slot = 1; slot <= 20; slot++) for (const key of [`class_${slot}`, `subclass_${slot}`]) {
        const id = readers.layerValue(playerBase.player_characters, key);
        if (fromBaseUuid(id)) classLevels.set(id.toLowerCase(), phpJsonInt(readers.layerValue(playerBase.player_characters, `class_level_${slot}`)));
    }
    const sourceClassLevel = (row: ItemFieldRow, source: unknown): number | null => {
        const held = classLevels.get(readItemUuidSlot(source).toLowerCase());
        if (held !== undefined) return held;
        // Provider walks can return bare passive references after the prefix's
        // class-level filter. Recover their authored class, never the PC's total.
        for (const key of ['requires_subclass', 'requires_class']) {
            const ids = entries(row, key).map(readLayerListEntryUuid).filter(fromBaseUuid);
            if (ids.length) return Math.max(...ids.map(id => classLevels.get(id.toLowerCase()) ?? 0));
        }
        return null;
    };
    const levelGate = (row: ItemFieldRow, source: unknown): boolean => meetsAbilityLevelRequirements({ isPlayerCharacter: Boolean(playerBase),
        totalLevel: phpJsonInt(readers.layerValue(host, 'total_level')),
        requiresLevel: readers.layerValue(row, 'requires_level') === null ? null : phpJsonInt(readers.layerValue(row, 'requires_level')),
        requiresClassLevel: readers.layerValue(row, 'requires_class_level') === null ? null : phpJsonInt(readers.layerValue(row, 'requires_class_level')),
        sourceClassLevel: sourceClassLevel(row, source),
        statusDelivered: Object.values(readCreatureStatuses(host, 'target', definition)).some(status => fromBaseRecord(status)
            && phpJsonString(readers.layerValue(status, 'status_id')).toLowerCase() === phpJsonString(source).toLowerCase()) });
    for (const grant of compileDefaultAbilityGrants({ hasEncounterCreatureId: phpJsonTruthy(readers.layerValue(host.encounter_creatures as ItemFieldRow ?? {}, 'encounter_creature_id')),
        itemType: playerBase ? 'player_character' : phpJsonString(readers.itemField(host, 'item_type')), legendaryCreature: phpJsonTruthy(readers.layerValue(host, 'legendary_creature')) }))
        appendCompiledLayerInPlace(host, grant.layer, { ability_id: grant.abilityId, item_name: grant.itemName, source: grant.source,
            requires_level: grant.requiresLevel, ...(grant.hidden ? { do_not_show_ability: 1 } : {}), item_arr: definition(grant.abilityId) });
    if (playerBase) appendCompiledLayerInPlace(host, 'full_ability', { ability_id: SHORT_REST, item_name: 'Short Rest', source: 'host', requires_level: 1, item_arr: definition(SHORT_REST) });
    const creatureId = readers.layerValue(host.encounter_creatures as ItemFieldRow ?? {}, 'encounter_creature_id');
    if (!phpJsonTruthy(creatureId)) return host;
    if (entries(host, 'menta_known').length)
        throw new FromBaseStageMissing('ability-spell-finalization', 'materialization:menta_known');
    const prepared = playerBase ? stored(playerBase as unknown as FromBaseRow, 'encounter_creatures_spells_prepared') : [];
    if (!Array.isArray(prepared)) throw new FromBaseInputMissing('saved-prepared-spell-rows', base.entity_id);
    // The ports write only grant buckets. On a form delta their ordered-array
    // window contains those buckets, never the actor, inventory or catalog.
    const portKeys = [...executable, 'passive_ability', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known'];
    const portLayers = ['at_will_spell', 'innate_spell_per_long_rest', 'spells_known'];
    const layers = fromBaseRecord(host.item_layer_types) ? host.item_layer_types : {};
    const portInput = owned ? { ...Object.fromEntries(portKeys.filter(key => key in host).map(key => [key, host[key]])),
        item_layer_types: Object.fromEntries(portLayers.filter(key => key in layers).map(key => [key, layers[key]])) } : host;
    const ref = { value: fromJson(portInput) as unknown };
    const portHosts = new WeakSet<object>();
    const adoptPortWrites = (raw: object): ItemFieldRow => {
        const projected = nativePhpJson(raw) as FromBaseRow;
        for (const key of portKeys) {
            if (key in projected) host[key] = projected[key]; else delete host[key];
        }
        const projectedLayers = fromBaseRecord(projected.item_layer_types) ? projected.item_layer_types : {};
        for (const key of portLayers) {
            if (key in projectedLayers) layers[key] = projectedLayers[key]; else delete layers[key];
        }
        return host;
    };
    if (owned && ref.value instanceof PhpArray) portHosts.add(ref.value);
    let nativeReads = new WeakMap<object, ItemFieldRow>();
    const native = (raw: unknown): ItemFieldRow | null => {
        if (raw == null) return null;
        if (typeof raw !== 'object') return nativePhpJson(raw) as ItemFieldRow;
        let value = nativeReads.get(raw);
        if (value === undefined) { value = owned && portHosts.has(raw) ? adoptPortWrites(raw) : nativePhpJson(raw) as ItemFieldRow; nativeReads.set(raw, value); }
        return value;
    };
    // Keep the two ordered-array ports in their native representation between
    // reads; array-path.get otherwise converts the entire host on every access.
    type Env = Parameters<typeof ftProjectFeatGrantedExecutableAbilities>[1];
    const env = { deps: {} } as Env;
    env.deps = {
        // The ports notify every host write, including edits to nested buckets.
        // Their consecutive read callbacks can then share one owned JSON view.
        host_changed: () => { if (owned && ref.value instanceof PhpArray) portHosts.add(ref.value); nativeReads = new WeakMap(); },
        item_field: (row, key) => readers.itemField(native(row), key), layer_entries: (row, key) => readers.layerEntries(native(row), key),
        layer_has: (row, key) => has(native(row), key),
        ability_entry_id: row => idOf(native(row)), layer_list_entry_uuid: row => readLayerListEntryUuid(native(row)),
        ft_spell_grant_row_sources: (row, input) => ftSpellGrantRowSources(row, input, env),
        ft_resolve_layer_entry: (entry, input) => resolveLayerEntry(native(entry), native(input)!, definition),
        check_ability_conditions: (row, input, equipment, skills) => conditions.check(native(row), native(input)!, equipment, skills),
        meet_ability_requirements: (row, entry) => levelGate(native(row)!, sourceOf(native(entry), '')),
        check_uuid: fromBaseUuid, ft_spell_grant_source_counted: (input, layer, id, source) => ftSpellGrantSourceCounted(input, layer, id, source, env),
        layer_append: (input, key, entry) => {
            const row = native(input.value) as FromBaseRow;
            appendCompiledLayerInPlace(row, key, native(entry));
            if (owned && input.value instanceof PhpArray) input.value.set(key, fromJson(row[key]));
            else input.value = fromJson(row);
            nativeReads = new WeakMap();
        },
        contains_trigger_key: containsAbilityTriggerKey, _ft_resolve_carrier_ref: definition,
        passive_ability_entry_is_active: entry => stored(native(entry), 'passive_conditions_active') !== false,
        ft_layer_entry_needs_resolve: entry => layerEntryNeedsResolve(native(entry)), ft_hoist_bucketed_spells_known: input => { ftHoistBucketedSpellsKnown(input, env); },
    };
    ftProjectFeatGrantedExecutableAbilities(ref, env);
    ftProjectCarrierAndPassiveHostedSpellGrants(ref, env);
    host = native(ref.value) as FromBaseRow;
    for (const entry of entries(host, 'passive_ability')) {
        const passive = resolve(entry), source = fromBaseRecord(entry) ? stored(entry, 'source') : '';
        if (!passive || (fromBaseRecord(entry) && stored(entry, 'passive_conditions_active') === false)
            || !conditions.check(passive, host, false, false) || !levelGate(passive, source)) continue;
        const passiveId = phpJsonString(readers.itemField(passive, 'item_id'));
        for (const type of executable) for (const child of entries(passive, type)) {
            const ability = resolve(child), id = ability ? phpJsonString(readers.itemField(ability, 'item_id')) : '';
            if (!fromBaseUuid(id) || entries(host, type).some(row => idOf(row).toUpperCase() === id.toUpperCase())) continue;
            appendCompiledLayerInPlace(host, type, { ability_id: id, item_name: readers.itemField(ability, 'item_name') ?? 'Unknown',
                source: passiveId || 'NA', requires_level: phpJsonInt(fromBaseRecord(entry) ? stored(entry, 'requires_level') ?? 1 : 1),
                item_arr: ability });
        }
    }
    const { charge, materialize } = createActorAbilityMaterializer(base, metadata, host, context);
    const creature = (host.computed as FromBaseRow).creature as FromBaseRow;
    const derived = fromBaseRecord(creature.skill_ability) ? creature.skill_ability : {};
    for (const type of [...metadata.abilityTypes, 'sanguine']) {
        const raw = list(host, type), seen = new Set(raw.map(idOf).map(id => id.toUpperCase()));
        for (const entry of list(derived, type)) { const id = idOf(entry).toUpperCase(); if (id && !seen.has(id)) { raw.push(entry); seen.add(id); } }
        if (!raw.length) continue;
        const built: FromBaseRow[] = [];
        for (const entry of raw) {
            const id = idOf(entry), nested = fromBaseRecord(entry) ? stored(entry, 'item_arr') : null;
            // build_ability_array receives only item_arr from an array entry.
            // Derived skill display shells must resolve their executable definition.
            const ability = nested !== null && typeof nested === 'object' && Object.keys(nested).length
                ? resolve(entry) : fromBaseUuid(id) ? definition(id) : null;
            const source = sourceOf(entry, 'NA');
            if (ability && phpJsonTruthy(readers.layerValue(ability, 'draw_sheathed_weapon_for_attack_ability'))
                && (!fromBaseUuid(source) || !conditions.check({ ...ability, _draw_bound_inventory_id: [source] }, host, false))) continue;
            const projected = materialize(entry, id, ability);
            built.push({ item_arr: ability ?? [], source: projected.source, requires_level: projected.requiresLevel,
                ...(projected.consumeNestedUnitPerUse ? { consume_nested_unit_per_use: true } : {}), ...(projected.charges ? { charges: projected.charges } : {}) });
        }
        host[type] = built.sort((a, b) => phpJsonInt(stored(a, 'requires_level') ?? 1) - phpJsonInt(stored(b, 'requires_level') ?? 1));
    }
    // Native 2C appends materialized curse definitions to the class/lineage book.
    const curses = entries(host, 'curse').flatMap(entry => {
        if (fromBaseRecord(entry) && has(entry, 'item_arr')) return [stored(entry, 'item_arr')];
        return fromBaseUuid(entry) ? [definition(entry)] : [];
    });
    if (curses.length) host.curses_known = [...list(host, 'curses_known'), ...curses].sort((a, b) =>
        phpJsonInt(stored(a as ItemFieldRow, 'requires_class_level')) - phpJsonInt(stored(b as ItemFieldRow, 'requires_class_level')));
    const materializeSpells = (type: 'at_will_spell' | 'innate_spell_per_long_rest'): void => {
        const raw = list(host, type);
        if (!raw.length) return;
        if (type === 'at_will_spell') host[type] = raw.map(entry => ({ item_arr: resolve(entry) ?? [],
            source: sourceOf(entry, sourceItemId), requires_level: 1 }));
        else host[type] = groupInnateSpells({ itemId: sourceItemId, entries: raw.map(entry => {
            const ability = resolve(entry), id = idOf(entry);
            return { spellId: id, spellName: phpJsonString(readers.itemField(ability, 'item_name')), source: phpJsonString(sourceOf(entry, sourceItemId)),
                stackedSourceMaxes: fromBaseRecord(entry) && fromBaseRecord(stored(entry, 'stacked_source_maxes')) ? stored(entry, 'stacked_source_maxes') as Record<string, number> : {},
                maxCharges: fromBaseRecord(entry) && fromBaseRecord(stored(entry, 'charges')) ? phpJsonInt(stored(stored(entry, 'charges') as ItemFieldRow, 'max') ?? 1) : 1 };
        }) }).map(group => ({ item_arr: definition(group.spellId) ?? [], source: group.firstSource, stacked_source_maxes: group.sourceMaxes, requires_level: 1,
            charges: { current: phpJsonInt(charge(group.spellId, group.maxUses)), max: group.maxUses, resets_long_rest: 1 } }));
    };
    materializeSpells('at_will_spell');
    // Retain grant provenance, rather than infer it from the active prepared list on its next edit.
    if (playerBase) ((host.computed as FromBaseRow)._cache as FromBaseRow).spell_book = entries(host, 'spells_known').map(raw => {
        const row = raw as ItemFieldRow;
        return { spell_id: phpJsonString(readers.itemField(row, 'item_id')),
            class_id: readItemUuidSlot(readers.layerValue(row, 'class_id')),
            subclass_id: readItemUuidSlot(readers.layerValue(row, 'subclass_id')) };
    });
    const selected = prepared.filter(row => phpJsonInt(readers.layerValue(row, 'spell_list')) ===
        phpJsonInt(readers.layerValue(host.encounter_creatures as ItemFieldRow, 'spell_list_prepared') ?? 1));
    if (playerBase && selected.length) {
        const finalize = createPlayerDefinitionFinalizer(playerBase, metadata, 'ability-spell-finalization');
        const existing = entries(host, 'spells_known').map(row => phpJsonString(readers.itemField(row as ItemFieldRow, 'item_id')));
        const candidates = selected.map((row, index) => {
            const id = readItemUuidSlot(readers.layerValue(row, 'spell_id'));
            const spell = fromBaseUuid(id) && !existing.includes(id) ? finalize(id, 'prepared-spell') : null;
            return { rowToken: String(index), spellId: id, resolved: spell !== null,
                resolvedItemId: phpJsonString(readers.itemField(spell, 'item_id')), spellToken: String(index), subclassIds: null, spell };
        });
        for (const plan of materializePlayerPreparedSpells({ existingSpellIds: existing, candidates })) {
            const row = selected[Number(plan.rowToken)] as ItemFieldRow;
            appendCompiledLayerInPlace(host, 'spells_known', constructPlayerPreparedSpellEntry(row,
                candidates[Number(plan.spellToken)]!.spell!, plan.subclassIds, readers.layerValue));
        }
    }
    if (!playerBase && readers.itemField(host, 'item_type') === 'creature') {
        const existing = new Set(entries(host, 'spells_known').map(row => phpJsonString(readers.itemField(row as ItemFieldRow, 'item_id'))));
        for (const entry of entries(host, 'spell_prepared_1')) {
            const id = readLayerListEntryUuid(entry);
            if (!fromBaseUuid(id) || existing.has(id)) continue;
            const spell = definition(id);
            if (!spell || !phpJsonTruthy(readers.itemField(spell, 'item_id'))) continue;
            appendCompiledLayerInPlace(host, 'spells_known', { spell_id: [id], subclass_id: [''], ...fromBaseClone(spell) });
            existing.add(id);
        }
    }
    host._ft_prepared_spells_loaded = [1];

    materializeSpells('innate_spell_per_long_rest');
    for (const type of [...metadata.abilityTypes, 'at_will_spell', 'curses_known']) {
        const rows = list(host, type); if (!rows.length) continue;
        const chosen = finalizeNonChargeAbilityBucket({ layer: type, entries: rows.map((entry, index) => {
            const row = fromBaseRecord(entry) ? entry : {}, ability = resolve(entry);
            return { token: String(index), abilityId: idOf(entry), source: phpJsonString(stored(row, 'source')),
                name: phpJsonString(readers.itemField(ability, 'item_name')), requiresLevel: phpJsonInt(stored(row, 'requires_level') ?? 1),
                drawsSheathedWeapon: phpJsonTruthy(readers.layerValue(ability, 'draw_sheathed_weapon_for_attack_ability')) };
        }) });
        host[type] = chosen.map(row => rows[Number(row.token)]);
    }
    for (const type of metadata.abilityTypes) {
        const rows = list(host, type); if (!rows.length) continue;
        host[type] = rows.filter(entry => {
            const ability = resolve(entry); if (!ability) return true;
            const allowed = skillGate(ability, host);
            if (!allowed && type === 'passive_ability' && fromBaseRecord(entry)) entry.passive_conditions_active = false;
            return (allowed || type === 'passive_ability' && fromBaseRecord(entry)) && levelGate(ability, fromBaseRecord(entry) ? stored(entry, 'source') : '');
        });
        if (!(host[type] as unknown[]).length) delete host[type];
    }
    const passives = list(host, 'passive_ability');
    if (passives.length) host.passive_ability = passives.map(entry => {
        if (!fromBaseRecord(entry)) return entry;
        const ability = resolve(entry);
        return ability ? { ...entry, passive_conditions_active: conditions.check(ability, host, false, false) } : entry;
    });
    creature.overrun_charges_remaining = compileCreatureOverrunChargesRemaining({ hasEncounterCreatureId: true,
        freeAbilities: list(host, 'free_ability').map(entry => ({ abilityId: idOf(entry), charges: fromBaseRecord(entry) && fromBaseRecord(stored(entry, 'charges'))
            ? stored(stored(entry, 'charges') as ItemFieldRow, 'current') as number | string | null : 'NA' })) });
    return host;
}
